import React, { useState } from 'react';
import { toast } from 'sonner';
import { apiClient, getApiBase } from '../lib/api';
import { formatMxn } from '../lib/superApi';
import { CreditCard, Landmark, Loader2 } from 'lucide-react';

const API = getApiBase();

export default function SubscriptionPaymentPanel({ subscription, onUpdated, compact = false }) {
  const [mpLoading, setMpLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [proofNote, setProofNote] = useState('');

  if (!subscription) return null;

  const interval = subscription.billing_interval || 'monthly';
  const amount = interval === 'annual' ? subscription.annual_amount : subscription.monthly_amount;
  const intervalLabel = interval === 'annual' ? 'anual' : 'mensual';

  const payWithMp = async () => {
    setMpLoading(true);
    try {
      const { data } = await apiClient.post(`${API}/admin/subscription/checkout/mp`);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error('No se recibió URL de pago');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al iniciar pago con Mercado Pago');
    } finally {
      setMpLoading(false);
    }
  };

  const reportTransfer = async () => {
    setTransferLoading(true);
    try {
      await apiClient.post(`${API}/admin/subscription/checkout/transfer`, { proof_note: proofNote });
      toast.success('Transferencia reportada. El equipo de DanySolutions la revisará.');
      setProofNote('');
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo reportar la transferencia');
    } finally {
      setTransferLoading(false);
    }
  };

  const wrapperClass = compact
    ? 'rounded-md border border-amber-200 bg-amber-50/80 p-4 space-y-3'
    : 'rounded-lg border border-slate-200 bg-white p-6 space-y-4 max-w-lg w-full shadow-sm';

  return (
    <div className={wrapperClass} data-testid="subscription-payment-panel">
      {subscription.pending_transfer && (
        <p className="text-xs text-amber-800 border border-amber-300 rounded-md px-3 py-2 bg-amber-50">
          Tienes una transferencia en revisión. Te avisaremos cuando se confirme.
        </p>
      )}
      <div className="text-sm text-slate-700">
        Renovación {intervalLabel}:{' '}
        <span className="text-teal-700 font-semibold">{formatMxn(amount || 0)}</span>
      </div>
      {subscription.platform_mp_configured && (
        <button
          type="button"
          onClick={payWithMp}
          disabled={mpLoading}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-teal-600 hover:bg-teal-500 text-white text-sm py-2.5 disabled:opacity-50"
        >
          {mpLoading ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
          Pagar con Mercado Pago
        </button>
      )}
      {(subscription.transfer_instructions || '').trim() && (
        <div className="space-y-2 border-t border-slate-200 pt-3">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <Landmark size={14} /> Transferencia
          </div>
          <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">
            {subscription.transfer_instructions}
          </pre>
          <textarea
            className="w-full text-xs min-h-[72px] rounded-md border border-slate-300 px-3 py-2"
            placeholder="Referencia o nota opcional (folio, hora, banco…)"
            value={proofNote}
            onChange={(e) => setProofNote(e.target.value)}
          />
          <button
            type="button"
            onClick={reportTransfer}
            disabled={transferLoading || subscription.pending_transfer}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 text-slate-700 text-xs py-2 hover:bg-slate-50 disabled:opacity-50"
          >
            {transferLoading ? <Loader2 className="animate-spin" size={14} /> : null}
            Reportar transferencia
          </button>
        </div>
      )}
      {!subscription.platform_mp_configured && !(subscription.transfer_instructions || '').trim() && (
        <p className="text-xs text-slate-500">
          Aún no hay métodos de pago configurados. Contacta a DanySolutions.
        </p>
      )}
    </div>
  );
}
